<template>
  <div v-if="changePassword" class="login-container">
    <div class="washi-title">{{ $t('login.title') }}</div>
    <div v-if="errorCode === 401" class="error">
      {{ $t('login.wrongCredentials') }}
    </div>
    <div v-else-if="errorCode === 429" class="error">
      {{ $t('login.tooManyRequests') }}
    </div>
    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.password') }}</div>
      <input
        ref="password"
        v-model="password"
        type="password"
        class="washi-input"
        :data-value="!!password"
        @keyup="checkSecondPassword"
      />
    </div>
    <div
      class="password-checker"
      :class="{
        error: !(minimumLetters || hasUppercase || specialCharacter || oneNumber) && isSend,
      }"
    >
      <div
        class="title"
        :class="{
          red: !(minimumLetters || hasUppercase || specialCharacter || oneNumber) && isSend,
        }"
      >
        {{ $t('register.password.shouldContain') }}
      </div>
      <div class="line" :class="{ error: isSend && !minimumLetters, checked: minimumLetters }">
        <div class="checkable"><check class="icon" /></div>
        <div class="value">{{ $t('register.password.minimumLetters') }}</div>
      </div>
      <div class="line" :class="{ error: isSend && !hasUppercase, checked: hasUppercase }">
        <div class="checkable"><check class="icon" /></div>
        <div class="value">{{ $t('register.password.capital') }}</div>
      </div>
      <div
        class="line"
        :class="{
          error: isSend && !(specialCharacter || oneNumber),
          checked: specialCharacter || oneNumber,
        }"
      >
        <div class="checkable"><check class="icon" /></div>
        <div class="value">{{ $t('register.password.numberOrSymbol') }}</div>
      </div>
    </div>
    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.passwordConfirm') }}</div>
      <input
        ref="password2"
        v-model="password2"
        type="password"
        class="washi-input"
        :data-value="!!password2"
        @keyup="checkSecondPassword"
      />
    </div>
    <div class="spacer" />
    <div class="sign-in main-button" :class="{ disabled: !password || !password2 }" @click="validatePassword">
      <span class="text">{{ $t('login.buttons.resetPassword') }}</span>
    </div>
  </div>
  <div v-else class="login-container" @keydown.enter="login">
    <div class="washi-title">{{ $t('login.title') }}</div>
    <div v-if="errorCode === 401" class="error">
      {{ $t('login.wrongCredentials') }}
    </div>
    <div v-else-if="errorCode === 429" class="error">
      {{ $t('login.tooManyRequests') }}
    </div>
    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.email') }}</div>
      <input ref="email" v-model="email" type="text" class="washi-input" :data-value="!!email" />
    </div>
    <div class="input-container">
      <div class="input-title">{{ $t('login.fields.password') }}</div>
      <input ref="password" v-model="password" type="password" class="washi-input" :data-value="!!password" />
    </div>
    <div class="link forgot-password" @click="passwordForgotten">
      {{ $t('login.fields.forgotPassword') }}
    </div>
    <div class="spacer" />
    <div class="sign-in main-button" :class="{ disabled: !email || !password || loading }" @click="login">
      <span class="text">{{ $t('login.buttons.login') }}</span>
    </div>
    <div class="spacer">
      <div class="divider" />
      <div class="text">ou</div>
      <div class="divider" />
    </div>
    <div class="main-button sign-in-google" :class="{ disabled: loading }" @click="googleSignIn">
      <googleLogo class="google-logo" />
      <div class="divider" />
      <span class="text">{{ $t('login.buttons.google') }}</span>
    </div>
    <div class="spacer">
      <div class="divider" />
    </div>
    <div class="no-account">
      {{ $t('login.noAccount') }}
      <router-link :to="{ name: 'register' }" class="link2">Sign up</router-link>
    </div>
  </div>
</template>

<script>
import check from '@/assets/icons/checked.svg';
import googleLogo from '@/assets/icons/google.svg';
import { requestCode } from 'vue-google-identity';

export default {
  name: 'LoginBody',
  components: {
    check,
    googleLogo,
  },
  props: {
    checkAction: {
      type: Function,
      required: true,
    },
    changePassword: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      email: '',
      password: '',
      password2: '',
      errorCode: 0,
      isSend: false,
    };
  },
  computed: {
    minimumLetters() {
      return this.password.length >= 8;
    },
    specialCharacter() {
      return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(this.password);
    },
    oneNumber() {
      return /[0123456789]+/.test(this.password);
    },
    hasUppercase() {
      return this.password.toLowerCase() !== this.password && this.password.toUpperCase() !== this.password;
    },
  },
  watch: {
    email() {
      this.error = false;
      if (this.$refs.email) {
        this.$refs.email.setCustomValidity('');
      }
      if (this.$refs.password) {
        this.$refs.password.setCustomValidity('');
      }
    },
    password() {
      this.error = false;
      if (this.$refs.email) {
        this.$refs.email.setCustomValidity('');
      }
      if (this.$refs.password) {
        this.$refs.password.setCustomValidity('');
      }
    },
    password2() {
      if (!this.$refs.password2) return;
      if (this.password2 === this.password) {
        this.$refs.password2.setCustomValidity('');
      } else {
        this.$refs.password2.setCustomValidity('Invalid');
      }
    },
  },
  methods: {
    checkValidation() {
      const checkerErrors = [];
      if (!this.minimumLetters) checkerErrors.push(0);
      if (!this.oneNumber && !this.specialCharacter) checkerErrors.push(2);
      if (!this.hasUppercase) checkerErrors.push(3);
      return this.password && checkerErrors.length === 0;
    },
    async checkSecondPassword() {
      if (this.loading || !this.isSend || !this.$refs.password2) return;
      if (this.checkValidation()) {
        this.$refs.password.setCustomValidity('');
        if (this.password === this.password2) {
          this.$refs.password2.setCustomValidity('');
        }
      }
    },
    async googleSignIn() {
      if (this.loading) return;
      this.loading = true;

      try {
        const response = await new Promise(async (resolve, reject) => {
          const res = await requestCode();
          if (res.error) {
            reject(new Error(res.error));
          } else {
            resolve(res);
          }
        });
        await this.$store.dispatch('auth.loginWithGoogle', {
          offlineCode: response.code,
        });
        // fetch async user info to populate store
        await this.checkAction();
        this.$store.dispatch('templates.fetch');
        this.$router.push({ name: 'dashboard' });
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    async login() {
      if (this.loading) return;
      this.loading = true;

      this.$refs.email.setCustomValidity('');
      this.$refs.password.setCustomValidity('');
      try {
        await this.$store.dispatch('auth.login', {
          email: this.email,
          password: this.password,
        });
        await this.checkAction();
        this.$router.push({ name: 'dashboard' });
      } catch (e) {
        if (e.response.status === 429) {
          this.errorCode = 429;
        } else if (e.response.status === 400 && e.response.data && e.response.data.code === 409001) {
          this.loading = false;
          this.googleSignIn();
        } else {
          this.errorCode = 401;
          this.$refs.email.setCustomValidity('Invalid');
          this.$refs.password.setCustomValidity('Invalid');
        }
        console.error(e);
      }
      this.loading = false;
    },
    async validatePassword() {
      if (this.password !== this.password2 || this.password === '') {
        return;
      }
      try {
        if (!this.checkValidation()) {
          this.$refs.password.setCustomValidity('Invalid');
          this.$refs.password2.setCustomValidity('Invalid');
          this.loading = false;
          return;
        }

        this.isSend = true;
        await this.$store.dispatch('auth.resetPassword', {
          token: this.$route.query.token,
          password: this.password,
        });
        this.$router.push({ name: 'dashboard' });
      } catch (e) {
        alert(e);
      }
    },
    async passwordForgotten() {
      this.loading = true;
      try {
        await this.$store.dispatch('auth.askResetPassword', this.email);
        this.$router.push({
          name: 'askResetPassword',
          query: { email: this.email },
        });
      } catch (e) {
        alert(e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.password-checker {
  align-self: flex-start;
  margin: 12px 0;
  padding-left: 16px;
  border-left: 1px solid colors.$light-blue-color;

  .title {
    color: colors.$light-blue-color;

    &.red {
      color: colors.$red-color;
    }
  }

  &.error .line {
    &:not(.checked) {
      color: colors.$red-color;
    }
  }

  &:not(.error) .line .value {
    font-weight: lighter;
  }

  .line {
    display: flex;
    margin: 12px 0;

    .checkable {
      width: 22px;
      height: 22px;
      margin-right: 8px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: colors.$lighter-yellow-color;
      transition: all 0.3s;

      .icon {
        display: none;
        transition: all 0.3s;
      }
    }
    &.checked {
      .checkable {
        background: colors.$light-blue-color;
      }

      .icon {
        display: block;
        color: #fff !important;
        width: 8px;
        height: 8px;
      }
    }

    .value {
      transition: all 0.3s;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: normal;
    }

    &.error {
      color: colors.$red-color;
    }
  }
}
.input-container {
  margin: 8px 0;
  width: 100%;
  .input-title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 4px;
  }
}
.login-container {
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 128px;
  box-sizing: border-box;

  .washi-title {
    align-self: flex-start;
    font-size: 36px;
    font-weight: bold;
    margin-bottom: 45px;
  }
  .link {
    font-style: italic;
    font-size: 16px;
    font-weight: 300;
    text-align: right;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .link2 {
    color: colors.$secondary-color;
    font-weight: bold;
    &:hover {
      text-decoration: underline;
    }
  }
  .error {
    color: colors.$red-color;
    font-size: 16px;
    margin: 12px 0;
  }
  .forgot-password {
    align-self: flex-end;
    margin-right: 8px;
  }
  .main-button {
    width: 184px;
    height: 42px;
    font-size: 14px;
    box-sizing: border-box;
    &.sign-in-google {
      display: flex;
      justify-content: space-around;
      padding: 0;
      padding-right: 12px;
      text-align: left;
      .google-logo {
        width: 22px;
        height: 100%;
        margin: 16px 0 16px 16px;
        flex-shrink: 0;
      }
      .divider {
        width: 1px;
        height: 100%;
        background-color: currentColor;
        flex-shrink: 0;
        margin: 0 8px;
      }
    }
  }
  .spacer {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin: 16px 0;
    min-height: 12px;
    .text {
      margin: 0 64px;
      font-size: 20px;
    }
    .divider {
      height: 1px;
      flex-grow: 1;
      background-color: colors.$secondary-color;
    }
  }
  .no-account {
    font-size: 16px;
  }

  @media screen and (max-width: 1184px) {
    width: 100% !important;
    padding: 0 26px !important;
    margin-bottom: 24px;
    min-width: 260px;

    .washi-title {
      font-size: 21px;
      margin-bottom: 20px;
    }
  }
}
</style>
